"use client";
import { Tabs, Tab, Popover, PopoverTrigger, PopoverContent } from "@heroui/react";
import { useState, useEffect, useMemo } from "react";
import "./index.css";

interface EmojiKeyboardProps {
  onEmojiSelect: (emoji: string) => void;
  children?: React.ReactNode;
}

const RECENT_EMOJIS_KEY = "recentEmojis";
const MAX_RECENT_EMOJIS = 32;

const EmojiKeyboard: React.FC<EmojiKeyboardProps> = ({
  onEmojiSelect,
  children,
}) => {
  const [visible, setVisible] = useState(false);
  const [recentEmojis, setRecentEmojis] = useState<string[]>([]);

  // Load recently used emojis from localStorage
  useEffect(() => {
    const savedRecent = localStorage.getItem(RECENT_EMOJIS_KEY);
    if (savedRecent) {
      setRecentEmojis(JSON.parse(savedRecent));
    }
  }, []);

  // Add emoji to recently used
  const addToRecent = (emoji: string) => {
    setRecentEmojis((prev) => {
      // Remove existing same emoji
      const filtered = prev.filter((e) => e !== emoji);
      // Add to beginning
      const newRecent = [emoji, ...filtered].slice(0, MAX_RECENT_EMOJIS);
      // Save to localStorage
      localStorage.setItem(RECENT_EMOJIS_KEY, JSON.stringify(newRecent));
      return newRecent;
    });
  };

  // Handle emoji selection
  const handleEmojiSelect = (emoji: string) => {
    addToRecent(emoji);
    onEmojiSelect(emoji);
    setVisible(false);
  };



  // Dynamically set initial category (show recent if there are recently used emojis, otherwise show smileys)
  const [selectedCategory, setSelectedCategory] = useState<string>(
    recentEmojis.length > 0 ? "recent" : "faces"
  );

  // Update category data when recently used emojis are updated
  const categoryData: Record<string, string[]> = useMemo(() => {
    const categories = {
      recent: recentEmojis,
      ...EMOJI_DATA,
    };
    return categories;
  }, [recentEmojis]);

  const categoryIcons = {
    recent: "🕐",
    faces: "😊",
    gestures: "👍",
    hearts: "❤️",
    animals: "🐶",
    food: "🍕",
    activities: "🔥",
    objects: "📱",
    symbols: "💬",
  };

  const content = (
    <div className="emoji-keyboard">
      <div className="emoji-category-tabs">
        <Tabs aria-label="Tabs colors" color="primary" radius="full" onSelectionChange={(e) => setSelectedCategory(e as string)}>
          {Object.entries(categoryData).map(([category, emojis]) => {
            // If it's recent category and no emojis, don't show
            if (category === "recent" && emojis.length === 0) {
              return null;
            }

            return (
              <Tab key={category} title={categoryIcons[category as keyof typeof categoryIcons]} />
            );
          })}
        </Tabs>
      </div>
      <div className="emoji-grid">
        {selectedCategory === "recent" && recentEmojis.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p>No emojis used yet</p>
            <p>Used emojis will appear here</p>
          </div>
        ) : (
          categoryData[selectedCategory]?.map(
            (emoji: string, index: number) => (
              <button
                key={index}
                className="emoji-item"
                onClick={() => handleEmojiSelect(emoji)}
              >
                {emoji}
              </button>
            )
          )
        )}
      </div>
    </div>
  );

  return (
    <Popover
      isOpen={visible}
      onOpenChange={setVisible}
      placement="top-start"
    //   triggerType="menu"
    >
      <PopoverTrigger>
        {children || (
          <button className="emoji-trigger-button">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 14C8 14 9.5 16 12 16C14.5 16 16 14 16 14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 9H9.01"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15 9H15.01"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="emoji-keyboard-popover">
        {content}
      </PopoverContent>
    </Popover>
  );
};

export default EmojiKeyboard;

const EMOJI_DATA = {
  faces: [
    '😂',
    '😭',
    '😊',
    '😍',
    '🥰',
    '😘',
    '😉',
    '😎',
    '🤣',
    '😏',
    '😌',
    '🙂',
    '😀',
    '😃',
    '😄',
    '😁',
    '😆',
    '😅',
    '🤤',
    '😋',
    '🤪',
    '😜',
    '😝',
    '😛',
    '🤨',
    '🧐',
    '🤓',
    '🥳',
    '😇',
    '🤩',
    '🥺',
    '😢',
    '😤',
    '😠',
    '😡',
    '🤬',
    '😳',
    '🥵',
    '🥶',
    '😱',
    '😨',
    '😰',
    '😥',
    '😓',
    '🤗',
    '🤔',
    '🤭',
    '🤫',
    '🤥',
    '😶',
    '😐',
    '😑',
    '😬',
    '🙄',
    '😯',
    '😦',
    '😧',
    '😮',
    '😲',
    '🥱',
    '😴',
    '😪',
    '😵',
    '🤐',
    '🥴',
    '🤢',
    '🤮',
    '🤧',
    '😷',
    '🤒',
    '🤕',
  ],
  gestures: [
    '🙏',
    '👍',
    '👎',
    '👏',
    '🙌',
    '👋',
    '🤚',
    '🖐',
    '✋',
    '👌',
    '🤌',
    '🤏',
    '✌️',
    '🤞',
    '🤟',
    '🤘',
    '🤙',
    '👈',
    '👉',
    '👆',
    '🖕',
    '👇',
    '☝️',
    '✊',
    '👊',
    '🤛',
    '🤜',
    '👐',
    '🤝',
    '💪',
    '🤳',
    '✍️',
    '🤦',
    '🤷',
    '💅',
  ],
  hearts: [
    '❤️',
    '💕',
    '💞',
    '💓',
    '💗',
    '💖',
    '💘',
    '💝',
    '💟',
    '♥️',
    '💔',
    '❣️',
    '💜',
    '🧡',
    '💛',
    '💚',
    '💙',
    '🤍',
    '🖤',
    '🤎',
    '💋',
    '💌',
    '💐',
    '🌹',
    '🌸',
    '💍',
    '💎',
  ],
  animals: [
    '🐶',
    '🐱',
    '🐭',
    '🐹',
    '🐰',
    '🦊',
    '🐻',
    '🐼',
    '🐨',
    '🐯',
    '🦁',
    '🐮',
    '🐷',
    '🐸',
    '🐵',
    '🙈',
    '🙉',
    '🙊',
    '🐒',
    '🐔',
    '🐧',
    '🐦',
    '🐤',
    '🐣',
    '🐥',
    '🦆',
    '🦅',
    '🦉',
    '🦇',
    '🐺',
    '🐗',
    '🐴',
    '🦄',
    '🐝',
    '🐛',
    '🦋',
    '🐌',
    '🐞',
    '🐜',
    '🦟',
    '🦗',
    '🕷',
    '🦂',
    '🐢',
    '🐍',
    '🦎',
    '🦖',
    '🦕',
    '🐙',
    '🦑',
    '🦐',
    '🦞',
    '🦀',
    '🐡',
    '🐠',
    '🐟',
    '🐬',
    '🐳',
    '🐋',
    '🦈',
  ],
  food: [
    '🍕',
    '🍔',
    '🌭',
    '🍟',
    '🌮',
    '🌯',
    '🥪',
    '🥓',
    '🍗',
    '🍖',
    '🥩',
    '🍳',
    '🥞',
    '🧇',
    '🥯',
    '🍞',
    '🥖',
    '🥨',
    '🧀',
    '🥚',
    '🧈',
    '🥛',
    '☕',
    '🍵',
    '🧃',
    '🥤',
    '🧋',
    '🍺',
    '🍻',
    '🥂',
    '🍷',
    '🥃',
    '🍸',
    '🍹',
    '🍾',
    '🧊',
    '🍎',
    '🍏',
    '🍊',
    '🍋',
    '🍌',
    '🍉',
    '🍇',
    '🍓',
    '🫐',
    '🍈',
    '🍒',
    '🍑',
    '🥭',
    '🍍',
    '🥥',
    '🥝',
    '🍅',
    '🫒',
    '🥑',
    '🌶',
    '🫑',
    '🥒',
    '🥬',
    '🥦',
    '🧄',
    '🧅',
    '🥔',
    '🍠',
    '🌽',
    '🥕',
    '🍰',
    '🎂',
    '🧁',
    '🥧',
    '🍫',
    '🍬',
    '🍭',
    '🍮',
    '🍯',
    '🍪',
    '🍩',
    '🍨',
    '🍦',
    '🥜',
    '🌰',
    '🍿',
  ],
  activities: [
    '🔥',
    '⭐',
    '🌟',
    '✨',
    '💯',
    '🎉',
    '🎊',
    '🎈',
    '🎁',
    '🎀',
    '🎂',
    '🎄',
    '🎃',
    '🎆',
    '🎇',
    '🎮',
    '🕹',
    '🎯',
    '🎱',
    '🎲',
    '🎰',
    '🃏',
    '🎭',
    '🎨',
    '🎬',
    '🎤',
    '🎧',
    '🎵',
    '🎶',
    '🎼',
    '🎹',
    '🥁',
    '🎷',
    '🎺',
    '🎸',
    '🎻',
    '⚽',
    '🏀',
    '🏈',
    '⚾',
    '🥎',
    '🎾',
    '🏐',
    '🏉',
    '🏓',
    '🏸',
    '🥅',
    '⛳',
    '🏹',
    '🎣',
    '🥊',
    '🥋',
    '🛹',
    '🛼',
    '🏆',
    '🥇',
    '🥈',
    '🥉',
    '🏅',
    '🎖',
  ],
  objects: [
    '📱',
    '💻',
    '⌨️',
    '🖥',
    '🖨',
    '📷',
    '📸',
    '📹',
    '🎥',
    '📞',
    '☎️',
    '📺',
    '📻',
    '⌚',
    '⏰',
    '⏱',
    '⏲',
    '🕰',
    '💡',
    '🔦',
    '🕯',
    '🔥',
    '💸',
    '💵',
    '💴',
    '💶',
    '💷',
    '💰',
    '💳',
    '💎',
    '🔧',
    '🔨',
    '⚒',
    '🛠',
    '⛏',
    '🔩',
    '⚙️',
    '🧰',
    '🔫',
    '💣',
    '🧨',
    '🪓',
    '🔪',
    '🗡',
    '⚔️',
    '🛡',
    '🚬',
    '⚰️',
    '🏺',
    '🔮',
    '📿',
    '💈',
    '⚗️',
    '🔭',
    '🔬',
    '🩹',
    '🩺',
    '💊',
    '💉',
    '🌡',
    '🧹',
    '🧺',
    '🧻',
    '🚽',
    '🚰',
    '🚿',
    '🛁',
    '🧼',
    '🪒',
    '🧽',
    '🧴',
    '🛎',
    '🔑',
    '🗝',
    '🚪',
    '🪑',
    '🛋',
    '🛏',
    '🧸',
    '🖼',
    '🛍',
    '🛒',
    '🎁',
    '🎈',
    '🎀',
    '🎊',
    '🎉',
    '✉️',
    '📩',
    '📨',
    '📧',
    '💌',
    '📦',
    '📪',
    '📫',
    '📬',
    '📭',
    '📮',
    '📯',
    '📜',
    '📃',
    '📄',
    '📑',
    '📊',
    '📈',
    '📉',
    '📆',
    '📅',
    '📇',
    '📋',
    '📁',
    '📂',
    '📓',
    '📔',
    '📒',
    '📕',
    '📗',
    '📘',
    '📙',
    '📚',
    '📖',
    '🔖',
    '🔗',
    '📎',
    '📐',
    '📏',
    '📌',
    '📍',
    '✂️',
    '🖊',
    '🖋',
    '✒️',
    '🖌',
    '🖍',
    '📝',
    '✏️',
    '🔍',
    '🔎',
    '🔏',
    '🔐',
    '🔒',
    '🔓',
  ],
  symbols: [
    '❤️',
    '💔',
    '✨',
    '⭐',
    '🌟',
    '💫',
    '🔥',
    '💥',
    '✅',
    '❌',
    '❓',
    '❗',
    '❕',
    '❔',
    '💯',
    '🔴',
    '🟠',
    '🟡',
    '🟢',
    '🔵',
    '🟣',
    '⚫',
    '⚪',
    '🟤',
    '🔶',
    '🔷',
    '🔸',
    '🔹',
    '🔺',
    '🔻',
    '💠',
    '🔘',
    '🔳',
    '🔲',
    '▪️',
    '▫️',
    '◾',
    '◽',
    '◼️',
    '◻️',
    '🟥',
    '🟧',
    '🟨',
    '🟩',
    '🟦',
    '🟪',
    '⬛',
    '⬜',
    '🟫',
    '💬',
    '💭',
    '🗯',
    '♠️',
    '♣️',
    '♥️',
    '♦️',
    '🃏',
    '🎴',
    '🔇',
    '🔈',
    '🔉',
    '🔊',
    '🔔',
    '🔕',
    '📣',
    '📢',
    '⬆️',
    '↗️',
    '➡️',
    '↘️',
    '⬇️',
    '↙️',
    '⬅️',
    '↖️',
    '↕️',
    '↔️',
    '↩️',
    '↪️',
    '⤴️',
    '⤵️',
    '🔃',
    '🔄',
    '🔙',
    '🔚',
    '🔛',
    '🔜',
    '🔝',
    '#️⃣',
    '*️⃣',
    '0️⃣',
    '1️⃣',
    '2️⃣',
    '3️⃣',
    '4️⃣',
    '5️⃣',
    '6️⃣',
    '7️⃣',
    '8️⃣',
    '9️⃣',
    '🔟',
  ],
}
